// src/pages/LandingPage.js
import React from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import LandingMarketTable from '../components/LandingMarketTable';
import metricDefinitions from '../constants/metricsDefinitions';

function formatNumber(n) {
  if (n == null) return '—';
  if (n >= 1e12) return `$${(n / 1e12).toFixed(1)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${n.toLocaleString()}`;
}

function stripHtmlToText(html) {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function definitionParagraphs(key) {
  const raw = metricDefinitions?.[key] || '';
  const text = stripHtmlToText(raw);
  if (!text) return [];
  return text
    .split('\n\n')
    .map((p) => p.trim())
    .filter(Boolean);
}

const GLOSSARY = [
  {
    title: 'Liquidity',
    items: [
      { key: 'currentRatio', label: 'Current ratio' },
      { key: 'quickRatio', label: 'Quick ratio' },
      { key: 'cashRatio', label: 'Cash ratio' },
    ],
  },
  {
    title: 'Profitability',
    items: [
      { key: 'grossProfitMargin', label: 'Gross profit margin' },
      { key: 'operatingProfitMargin', label: 'Operating profit margin' },
      { key: 'netProfitMargin', label: 'Net profit margin' },
      { key: 'ebitMargin', label: 'EBIT margin' },
      { key: 'ebitdaMargin', label: 'EBITDA margin' },
      { key: 'pretaxProfitMargin', label: 'Pretax profit margin' },
      { key: 'continuousOperationsProfitMargin', label: 'Continuing operations profit margin' },
      { key: 'bottomLineProfitMargin', label: 'Bottom-line profit margin' },
    ],
  },
  {
    title: 'Efficiency / operations',
    items: [
      { key: 'assetTurnover', label: 'Asset turnover' },
      { key: 'inventoryTurnover', label: 'Inventory turnover' },
      { key: 'receivablesTurnover', label: 'Receivables turnover' },
      { key: 'payablesTurnover', label: 'Payables turnover' },
      { key: 'fixedAssetTurnover', label: 'Fixed asset turnover' },
    ],
  },
  {
    title: 'Risk & leverage',
    items: [
      { key: 'debtToAssetsRatio', label: 'Debt-to-assets ratio' },
      { key: 'debtToEquityRatio', label: 'Debt-to-equity ratio' },
      { key: 'debtToCapitalRatio', label: 'Debt-to-capital ratio' },
      { key: 'longTermDebtToCapitalRatio', label: 'Long-term debt-to-capital ratio' },
      { key: 'financialLeverageRatio', label: 'Financial leverage ratio' },
    ],
  },
  {
    title: 'Cash flow',
    items: [
      { key: 'operatingCashFlowRatio', label: 'Operating cash flow ratio' },
      { key: 'operatingCashFlowSalesRatio', label: 'Operating cash flow / sales' },
      { key: 'freeCashFlowOperatingCashFlowRatio', label: 'Free cash flow / operating cash flow' },
      { key: 'operatingCashFlowPerShare', label: 'Operating cash flow per share' },
      { key: 'freeCashFlowPerShare', label: 'Free cash flow per share' },
    ],
  },
  {
    title: 'Valuation',
    items: [
      { key: 'priceToEarningsRatio', label: 'Price-to-earnings (P/E)' },
      { key: 'priceToSalesRatio', label: 'Price-to-sales (P/S)' },
      { key: 'priceToFreeCashFlowRatio', label: 'Price-to-free-cash-flow' },
      { key: 'priceToOperatingCashFlowRatio', label: 'Price-to-operating-cash-flow' },
      { key: 'priceToBookRatio', label: 'Price-to-book (P/B)' },
      { key: 'enterpriseValueMultiple', label: 'EV/EBITDA' },
    ],
  },
  {
    title: 'Per-share metrics',
    items: [
      { key: 'revenuePerShare', label: 'Revenue per share' },
      { key: 'netIncomePerShare', label: 'Net income per share (EPS)' },
      { key: 'cashPerShare', label: 'Cash per share' },
      { key: 'bookValuePerShare', label: 'Book value per share' },
      { key: 'tangibleBookValuePerShare', label: 'Tangible book value per share' },
      { key: 'shareholdersEquityPerShare', label: "Shareholders’ equity per share" },
    ],
  },
];

function LandingPage() {
  const [spy, setSpy] = React.useState(null);

  React.useEffect(() => {
    fetch('/data/SPY/landing.json')
      .then((r) => (r.ok ? r.json() : null))
      .then(setSpy)
      .catch(() => {});
  }, []);

  return (
    <div className="landing">
      {/* Responsible-use + disclaimer block */}
      <section className="landing-section">
        <div className="landing-content-card landing-content-card--disclaimer">
          <h2>Use this site as a research starting point</h2>
          <p>
            The charts and tables on StockVisualize are designed to help you explore and understand
            data, not to tell you what to buy or sell. Markets move quickly and data can change, so
            you should always cross-check important figures with company filings, broker data and
            other independent sources before acting.
          </p>
          <p>
            Nothing on StockVisualize.com is financial, investment, tax or legal advice, and
            nothing here should be treated as a recommendation or a solicitation to trade. You are
            responsible for your own investment decisions and for understanding the risks involved.
            If you are unsure, you should speak with a qualified financial adviser before making
            any commitments.
          </p>
        </div>
      </section>

      {/* Top ticker / market strip */}
      <div className="landing-ticker-bar">
        {spy ? (
          <span>
            <strong>SPDR S&P 500 ETF Trust</strong> · <strong>Price:</strong> ${spy.price.toFixed(2)} ·{' '}
            <strong>Change:</strong>{' '}
            <span className={spy.changePercent >= 0 ? 'pos' : 'neg'}>
              {spy.changePercent.toFixed(2)}%
            </span>{' '}
            · <strong>Market Cap:</strong> {formatNumber(spy.marketCap)} · <strong>Volume:</strong>{' '}
            {formatNumber(spy.volume)}
          </span>
        ) : (
          <span>Loading market snapshot…</span>
        )}
      </div>

      <LandingMarketTable />

      {/* Top content block for AdSense-optimised text */}
      <section className="landing-section">
        <div className="landing-content-card landing-content-card--hero">
          <h1 className="landing-hero-title">See stocks the way long-term investors do.</h1>

          <p>With StockVisualize you can:</p>
          <ul className="landing-bullets">
            <li>Explore individual stocks with price, volume and basic trend information.</li>
            <li>Graph profitability, liquidity, leverage and valuation ratios over time.</li>
            <li>Compare several companies in the same sector on a single screen.</li>
            <li>Build a short watchlist of tickers you want to monitor more closely.</li>
            <li>Filter the wider market using simple, rule-based screening criteria.</li>
          </ul>
        </div>
      </section>

      {/* Tool cards row */}
      <section className="landing-section">
        <div className="landing-tools-header">
          <h2>Choose a tool to get started</h2>
          <p>
            Each tool answers a slightly different question about the market. Pick the one that
            matches what you are trying to understand today.
          </p>
        </div>

        <div className="landing-tools-row">
          <article className="landing-tool-card">
            <p className="landing-tool-label">Visualise a single stock</p>
            <h3>
              <Link to="/visualization">Stock Visualization</Link>
            </h3>
            <p>
              See historical price, percentage moves and trading volume on interactive charts.
              Quickly understand volatility, trend direction and how actively the stock trades.
            </p>
          </article>

          <article className="landing-tool-card">
            <p className="landing-tool-label">Track fundamentals over time</p>
            <h3>
              <Link to="/ratio-visualization">Stock Ratio Visualization</Link>
            </h3>
            <p>
              Plot profitability, liquidity, leverage, cash-flow and valuation ratios. See whether
              a company is becoming more or less efficient, profitable or risky over time.
            </p>
          </article>

          <article className="landing-tool-card">
            <p className="landing-tool-label">Compare companies</p>
            <h3>
              <Link to="/comparison">Stock Comparison</Link>
            </h3>
            <p>
              Line up several tickers and compare prices and key metrics side by side. Useful
              when you are deciding between a small group of ideas in the same theme or sector.
            </p>
          </article>

          <article className="landing-tool-card">
            <p className="landing-tool-label">Monitor ideas</p>
            <h3>
              <Link to="/watchlist">Watchlist</Link>
            </h3>
            <p>
              Maintain a lightweight watchlist of interesting tickers. Check price levels and key
              ratios in one place without having to reload each stock separately.
            </p>
          </article>

          <article className="landing-tool-card">
            <p className="landing-tool-label">Filter the market</p>
            <h3>
              <Link to="/screener">Screener</Link>
            </h3>
            <p>
              Filter stocks by valuation, profitability, leverage and other fundamentals. Narrow a
              large universe down to a smaller list of companies worth deeper research.
            </p>
          </article>
        </div>
      </section>

      {/* Bottom content block for more descriptive copy / AdSense text */}
      <section className="landing-section">
        <div className="landing-content-card">
          <h2>How the metrics on StockVisualize work</h2>
          <p>
            Under the hood, StockVisualize groups financial ratios into themes such as liquidity,
            profitability, efficiency, risk and leverage, cash flow, valuation and per-share
            metrics. Liquidity ratios like the current ratio, quick ratio and cash ratio look at
            whether a company can cover its short-term obligations. Profitability margins show how
            much of each unit of revenue turns into profit.
          </p>
          <p>
            Efficiency ratios describe how well a business uses its assets, inventory and
            receivables to generate sales. Debt-related metrics shine a light on financial risk and
            capital structure. Cash-flow metrics focus on how much cash the core business generates
            and how much is left after investment. Valuation ratios such as price-to-earnings or
            price-to-sales help you judge how expensive a stock looks relative to those
            fundamentals, while per-share measures translate everything down to a single share.
          </p>
          <p>
            Throughout the app you will see short definitions and explanations beside each metric.
            These are written in plain language so you can quickly remind yourself what a ratio
            means before using it in a decision.
          </p>
        </div>
      </section>

      {/* Glossary (crawlable, plain HTML text) */}
      <section className="landing-section" aria-labelledby="landing-glossary-title">
        <div className="landing-content-card landing-content-card--glossary">
          <h2 id="landing-glossary-title">Glossary of stock metrics</h2>
          <p className="landing-glossary-intro">
            This glossary includes every metric definition currently used by StockVisualize. Each
            definition is rendered as plain HTML text so it is readable and crawlable.
          </p>

          <div className="landing-glossary-grid">
            {GLOSSARY.map((group) => (
              <div className="landing-glossary-category" key={group.title}>
                <h3 className="landing-glossary-category-title">{group.title}</h3>

                <dl className="landing-glossary-list">
                  {group.items.map(({ key, label }) => {
                    const paragraphs = definitionParagraphs(key);

                    return (
                      <div className="landing-glossary-row" key={key}>
                        <dt className="landing-glossary-term">{label}</dt>
                        <dd className="landing-glossary-def">
                          {paragraphs.length ? (
                            paragraphs.map((p, idx) => <p key={`${key}-${idx}`}>{p}</p>)
                          ) : (
                            <p>Definition coming soon.</p>
                          )}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default LandingPage;
